import React, { useState } from 'react';
import { useScrollSection } from '../hooks/useScrollSection';

interface HeaderProps {
  currentPage: string;
}

const Header: React.FC<HeaderProps> = ({ currentPage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const currentSection = useScrollSection();

  const navigateTo = (path: string) => {
    const url = path === 'home' ? '/' : `/${path}`;
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
    setIsMobileMenuOpen(false);
  };

  const scrollToForm = () => {
    // Scroll directly to form title (form is present on all pages)
    const formTitle = document.getElementById('contact-form');
    if (formTitle) {
      formTitle.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // Fallback: try again after a short delay
      setTimeout(() => {
        const formTitleRetry = document.getElementById('contact-form');
        if (formTitleRetry) {
          formTitleRetry.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  const menuItems = [
    { label: 'Home', path: 'home' },
    { label: 'Come funziona', path: 'come-funziona' },
    { label: 'Fornitori', path: 'fornitori' },
    { label: 'Chi Siamo', path: 'chi-siamo' },
  ];

  // Normalize currentPage for comparison
  const normalizedCurrentPage = currentPage === '' ? 'home' : currentPage;
  
  // Use scroll section info only on home page
  const isInBlueSection = currentSection.isBlue;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-lg transition-all duration-300 ${isInBlueSection ? 'bg-primary/10' : ''}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button 
            onClick={() => navigateTo('home')}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <img 
              src={isInBlueSection ? "/assets/logo-white.svg" : "/assets/logo-blue.svg"}
              alt="Synergy" 
              className="h-10 w-auto"
              loading="eager"
              fetchPriority="high"
            />
          </button>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigateTo(item.path)}
                className={`font-medium transition-all duration-200 relative group ${
                  normalizedCurrentPage === item.path 
                    ? (isInBlueSection ? 'text-tertiary' : 'text-primary')
                    : (isInBlueSection ? 'text-white hover:text-tertiary' : 'text-gray-700 hover:text-primary')
                }`}
              >
                {item.label}
                <span className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 w-0 group-hover:w-full ${
                  isInBlueSection ? 'bg-tertiary' : 'bg-primary'
                }`}></span>
              </button>
            ))}
            <button
              onClick={scrollToForm}
              className={`px-8 py-2.5 rounded-full font-semibold transition-colors duration-300 ${
                isInBlueSection 
                  ? 'bg-tertiary text-white hover:bg-tertiary/90' 
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              Prova ora
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-xl transition-colors`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg className={`w-6 h-6 ${isInBlueSection ? 'text-white' : 'text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className={`w-6 h-6 ${isInBlueSection ? 'text-white' : 'text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className={`pb-5 space-y-2`}>
            <div className="flex flex-col items-end space-y-2">
              {menuItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigateTo(item.path)}
                  className={`w-full text-right px-3 py-3 font-medium transition-all duration-200 relative group ${
                    normalizedCurrentPage === item.path 
                      ? (isInBlueSection ? 'text-tertiary' : 'text-primary')
                      : (isInBlueSection ? 'text-white hover:text-tertiary' : 'text-gray-700 hover:text-primary')
                  }`}
                >
                  {item.label}
                  {/* <span className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 w-0 group-hover:w-full ${
                    isInBlueSection ? 'bg-tertiary' : 'bg-primary'
                  }`}></span> */}
                </button>
              ))}
              <button
                onClick={scrollToForm}
                className={`px-8 py-2.5 rounded-full font-semibold transition-colors duration-300 ${
                  isInBlueSection 
                    ? 'bg-tertiary text-white hover:bg-tertiary/90' 
                    : 'bg-primary text-white hover:bg-primary/90'
                }`}
              >
                Prova ora
              </button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
